import React from 'react';
import { motion } from 'framer-motion';
import { FaCar, FaTshirt, FaCheck, FaClock, FaLeaf, FaShieldAlt, FaStar, FaWhatsapp, FaArrowRight } from 'react-icons/fa';

import { Link } from 'react-router-dom';

const ServicesPage = () => {
    const services = [
        {
            id: 'car-cleaning',
            icon: FaCar,
            title: 'Daily Car Cleaning',
            subtitle: 'Waterless Eco-Friendly Technology',
            description: 'Keep your car sparkling clean every day with our professional waterless car cleaning service. Our trained technicians use premium eco-friendly products that not only clean but also protect your car\'s paint finish.',
            image: '/images/hero-car.png',
            highlights: [
                '🚗 Weekly Once: Complete Interior Cleaning',
                '✨ Daily Exterior Cleaning (6 Days)',
            ],
            features: [
                'Daily exterior cleaning at your parking spot',
                'Premium waterless cleaning technology',
                'Eco-friendly, biodegradable products',
                'Interior dashboard wipe-down (weekly)',
                'Tire and rim cleaning',
                'Glass and mirror polishing',
            ],
            benefits: [
                { icon: FaClock, text: 'Saves 30+ minutes daily' },
                { icon: FaLeaf, text: 'Saves 15L water per wash' },
            ],
            pricing: [
                { type: '2 Wheeler', original: null, discounted: '₹200' },
                { type: 'Hatchback & Sedan', original: null, discounted: '₹500' },
                { type: 'Mid SUV & SUV', original: null, discounted: '₹600' },
                { type: 'Luxury Car', original: null, discounted: '₹700' },
            ],
            comboNote: '* Any combo packages can be availed for additional benefits',
        },
        {
            id: 'ironing',
            icon: FaTshirt,
            title: 'Steam Ironing Service',
            subtitle: 'Professional Garment Care',
            description: 'Say goodbye to wrinkled clothes and the hassle of ironing. Our professional steam ironing service ensures your clothes are perfectly pressed, fresh, and ready to wear. We handle all types of fabrics with care.',
            image: '/images/steam-ironing.png',
            features: [
                'Professional steam pressing technology',
                'Handles all fabric types',
                'Separate care for delicates',
                'Doorstep pickup and delivery',
                'Same-day service available',
                'CCTV monitored facility for security',
                'Eco-friendly starch options',
                'Button check and minor repairs',
            ],
            benefits: [
                { icon: FaClock, text: 'Ready by next morning' },
                { icon: FaStar, text: 'Premium finish quality' },
                { icon: FaShieldAlt, text: 'Fabric-safe process' },
            ],
        },
    ];

    return (
        <div className="min-h-screen pt-20 bg-white">
            {/* Hero Section */}
            <section className="section-padding bg-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 rounded-full blur-3xl opacity-20 bg-primary-200"></div>
                <div className="max-w-7xl mx-auto text-center relative z-10">
                    <motion.span
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="inline-block px-4 py-2 rounded-full bg-primary-50 text-primary-600 text-sm font-medium mb-6"
                    >
                        Our Services
                    </motion.span>
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
                    >
                        Premium Doorstep Services
                        <br />
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#3CCFDB] to-[#5AE07D] drop-shadow-md pb-2">For Modern Living</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-8"
                    >
                        From daily car cleaning to professional ironing, we bring convenience
                        to your doorstep. Explore our comprehensive range of services designed
                        to simplify your busy life.
                    </motion.p>

                    {/* Quick Navigation */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="flex flex-wrap justify-center gap-3"
                    >
                        {services.map((service) => (
                            <a
                                key={service.id}
                                href={`#${service.id}`}
                                className="px-5 py-3 rounded-full bg-gray-100 text-gray-700 hover:bg-primary-500 hover:text-white transition-all duration-300 flex items-center gap-2 font-medium"
                            >
                                <service.icon className="w-4 h-4" />
                                {service.title.split(' ')[0]}
                            </a>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Individual Services */}
            {services.map((service, index) => (
                <section
                    key={service.id}
                    id={service.id}
                    className={`section-padding ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
                >
                    <div className="max-w-7xl mx-auto">
                        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                            {/* Image */}
                            <motion.div
                                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                className={index % 2 === 1 ? 'lg:order-2' : ''}
                            >
                                <div className="relative rounded-3xl overflow-hidden shadow-2xl hover:shadow-3xl transition-shadow duration-500">
                                    <img
                                        src={service.image}
                                        alt={service.title}
                                        className="w-full h-80 lg:h-96 object-cover"
                                    />
                                    <div className="absolute top-4 left-4 p-4 bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg">
                                        <service.icon className="w-8 h-8 text-primary-600" />
                                    </div>
                                </div>
                            </motion.div>

                            {/* Content */}
                            <motion.div
                                initial={{ opacity: 0, x: index % 2 === 0 ? 50 : -50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                className={index % 2 === 1 ? 'lg:order-1' : ''}
                            >
                                <span className="inline-block px-3 py-1 rounded-full bg-primary-50 text-primary-600 text-xs font-bold uppercase tracking-wider mb-4">
                                    {service.subtitle}
                                </span>
                                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                                    {service.title}
                                </h2>
                                <p className="text-gray-600 leading-relaxed mb-6">
                                    {service.description}
                                </p>

                                {/* Highlights (for car cleaning) */}
                                {service.highlights && (
                                    <div className="flex flex-wrap gap-3 mb-6">
                                        {service.highlights.map((highlight) => (
                                            <div key={highlight} className="px-4 py-2 rounded-full bg-primary-50 border border-primary-100 text-primary-700">
                                                <span className="font-semibold text-sm">{highlight}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Features */}
                                <div className="grid grid-cols-2 gap-3 mb-6">
                                    {service.features.slice(0, 6).map((feature) => (
                                        <div key={feature} className="flex items-start gap-2">
                                            <FaCheck className="w-4 h-4 text-primary-500 mt-1 flex-shrink-0" />
                                            <span className="text-gray-600 text-sm">{feature}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Benefits */}
                                <div className="flex flex-wrap gap-4 mb-8">
                                    {service.benefits.map((benefit) => (
                                        <div key={benefit.text} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-200 shadow-sm">
                                            <benefit.icon className="w-4 h-4 text-primary-500" />
                                            <span className="text-gray-700 text-sm font-medium">{benefit.text}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Pricing Preview - Only show if pricing exists */}
                                {service.pricing && service.pricing.length > 0 && (
                                    <div className="p-4 rounded-xl mb-4 bg-white border border-gray-200 shadow-md">
                                        <h4 className="text-gray-900 font-semibold text-sm mb-3 flex items-center gap-2">
                                            <span className="w-1.5 h-4 bg-primary-500 rounded-full"></span>
                                            Pricing Plans
                                        </h4>
                                        <div className="grid grid-cols-4 gap-2">
                                            {service.pricing.slice(0, 4).map((price) => (
                                                <div key={price.type} className="p-2 rounded-lg bg-gray-50 border border-gray-100 hover:border-primary-200 transition-colors text-center">
                                                    <p className="text-gray-500 text-[10px] font-bold uppercase tracking-wide mb-0.5">{price.type}</p>
                                                    <p className="font-bold flex items-baseline justify-center">
                                                        {price.original && (
                                                            <span className="text-gray-400 line-through text-xs mr-1">{price.original}</span>
                                                        )}
                                                        <span className="text-lg text-gray-900">{price.discounted}</span>
                                                        <span className="text-[10px] text-gray-500 font-normal ml-0.5">/mo</span>
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                        {service.comboNote && (
                                            <p className="text-primary-600 text-xs font-medium mt-2 pt-2 border-t border-gray-100 flex items-center gap-1">
                                                <FaStar className="w-3 h-3" />
                                                {service.comboNote}
                                            </p>
                                        )}
                                    </div>
                                )}


                                <a
                                    href="https://wa.me/919059651065"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-primary inline-flex items-center shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                                >
                                    <FaWhatsapp className="mr-2 text-xl" />
                                    Book This Service
                                </a>
                            </motion.div>
                        </div>
                    </div>
                </section>
            ))}

            {/* CTA Section */}
            <section className="section-padding bg-gradient-to-r from-primary-600 to-primary-800">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.h2
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-3xl md:text-4xl font-bold text-white mb-6"
                    >
                        Need a Custom Package?
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-white/90 text-lg mb-8"
                    >
                        We offer customized packages for communities, apartments, and organizations.
                        Contact us for special rates and tailored solutions.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="flex flex-col sm:flex-row gap-4 justify-center"
                    >
                        <a
                            href="https://wa.me/919059651065"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center px-8 py-4 rounded-full font-semibold bg-white text-primary-600 hover:bg-white/90 transition-all shadow-lg"
                        >
                            Get Custom Quote
                        </a>
                        <Link
                            to="/contact"
                            className="inline-flex items-center justify-center px-8 py-4 rounded-full font-semibold bg-white text-primary-600 hover:bg-white/90 transition-all shadow-lg"
                        >
                            Partner With Us
                            <FaArrowRight className="ml-2" />
                        </Link>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default ServicesPage;
